import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DEFAULT_MIN_TEEN_AGE,
  OtpChannel,
  PASSWORD_MIN_LENGTH,
  US_STATES,
  UserRole,
  emailSchema,
  passwordSchema,
  phoneSchema,
  registerInputSchema,
  zipCodeSchema,
  type RegisterInput,
} from '@teentack/shared';
import { presentApiError } from '@/lib/form-errors';
import { useAuth } from '@/auth/use-auth';
import { useSkills } from '@/hooks/use-skills';
import { ErrorNote, InfoNote } from '@/components/Form';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput, Textarea } from '@/components/ui/Input';
import {
  ArrowRightIcon,
  BellIcon,
  BriefcaseIcon,
  ChatIcon,
  ShieldCheckIcon,
  SparkleIcon,
} from '@/components/ui/Icons';
import { SignupLayout } from '@/components/signup/SignupLayout';
import { Stepper, type StepDescriptor } from '@/components/signup/Stepper';
import { OptionCard } from '@/components/signup/OptionCard';
import { PasswordMeter } from '@/components/signup/PasswordMeter';
import { SkillPicker } from '@/components/signup/SkillPicker';
import { AvailabilityFields } from '@/components/fields/AvailabilityFields';
import {
  EMPTY_AVAILABILITY,
  toAvailability,
  toWorkPreferences,
  type AvailabilityValue,
} from '@/components/fields/availability-vocabulary';

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                 */
/* -------------------------------------------------------------------------- */

type SelfServeRole = typeof UserRole.TEEN | typeof UserRole.PARENT | typeof UserRole.JOB_POSTER;

/** The three roles that may self-register. Admins are provisioned. [FR-M01.5] */
const ROLE_OPTIONS = [
  {
    value: UserRole.TEEN,
    icon: <SparkleIcon className="size-5" />,
    title: 'I am a teen looking for work',
    description: 'Build a profile, list what you are good at and apply to local jobs.',
  },
  {
    value: UserRole.PARENT,
    icon: <ShieldCheckIcon className="size-5" />,
    title: 'I am a parent or guardian',
    description: 'Approve your teen, see where they work and stay in the loop.',
  },
  {
    value: UserRole.JOB_POSTER,
    icon: <BriefcaseIcon className="size-5" />,
    title: 'I am hiring',
    description: 'Post a job and meet verified, parent-approved teens nearby.',
  },
] as const;

/* -------------------------------------------------------------------------- */
/* Steps                                                                      */
/* -------------------------------------------------------------------------- */

type StepId = 'role' | 'about' | 'work' | 'account';

const STEP_TITLES: Record<StepId, string> = {
  role: 'Account type',
  about: 'About you',
  work: 'Skills',
  account: 'Sign-in details',
};

/** Teens have an extra step; the other two roles go straight to credentials. */
function stepsFor(role: SelfServeRole): StepId[] {
  return role === UserRole.TEEN
    ? ['role', 'about', 'work', 'account']
    : ['role', 'about', 'account'];
}

/** Which step owns each field, so a failed submit can jump back to it. */
const STEP_OF_FIELD: Record<string, StepId> = {
  firstName: 'about',
  lastName: 'about',
  dateOfBirth: 'about',
  parentEmail: 'about',
  professionalTitle: 'about',
  streetAddress: 'about',
  city: 'about',
  state: 'about',
  zipCode: 'about',
  bio: 'about',
  skillIds: 'work',
  availability: 'work',
  workPreferences: 'work',
  resumeUrl: 'work',
  email: 'account',
  phone: 'account',
  password: 'account',
  otpChannel: 'account',
};

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Whole years elapsed, evaluated in UTC — matches the api's own arithmetic. */
function ageInYears(iso: string): number | null {
  const dob = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - dob.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getUTCDate() < dob.getUTCDate())) age -= 1;
  return age;
}

/** Drops the optional fields the registrant left blank. */
function trimmed(value: string): string | undefined {
  const next = value.trim();
  return next.length > 0 ? next : undefined;
}

const EMPTY_FORM = {
  email: '',
  phone: '',
  password: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  parentEmail: '',
  professionalTitle: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  bio: '',
  resumeUrl: '',
};

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const skillsQuery = useSkills();
  const [params] = useSearchParams();

  // The guardian invite email links here as
  // /register?role=PARENT&email=…, so the parent lands on the right role with
  // their address already filled in.
  const invitedRole = params.get('role');
  const invitedEmail = params.get('email');

  const [role, setRole] = useState<SelfServeRole>(
    invitedRole === UserRole.PARENT || invitedRole === UserRole.JOB_POSTER
      ? invitedRole
      : UserRole.TEEN,
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(() =>
    invitedEmail ? { ...EMPTY_FORM, email: invitedEmail } : EMPTY_FORM,
  );
  const [otpChannel, setOtpChannel] = useState<OtpChannel>(OtpChannel.EMAIL);
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [work, setWork] = useState<AvailabilityValue>(EMPTY_AVAILABILITY);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  /** Set when the server says the email or mobile is already registered. */
  const [accountExists, setAccountExists] = useState(false);
  const [busy, setBusy] = useState(false);

  // The alert sits at the top of the card while the button sits at the bottom,
  // so on a long step it would otherwise appear off-screen and look like
  // nothing happened.
  const alertRef = useRef<HTMLDivElement>(null);

  const steps = stepsFor(role);
  const step = steps[stepIndex] ?? 'role';
  const isLastStep = stepIndex === steps.length - 1;

  const age = form.dateOfBirth ? ageInYears(form.dateOfBirth) : null;
  const isMinor = age !== null && age < 18;

  const stepDescriptors: StepDescriptor[] = steps.map((id) => ({ id, title: STEP_TITLES[id] }));

  /* --- editing ----------------------------------------------------------- */

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [key]: value }));
    if (key === 'email' || key === 'phone') {
      setAccountExists(false);
      setError(null);
    }
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  function chooseRole(next: SelfServeRole): void {
    setRole(next);
    setFieldErrors({});
    setError(null);
  }

  /* --- payload ----------------------------------------------------------- */

  function buildInput(): unknown {
    const address = {
      city: trimmed(form.city),
      state: trimmed(form.state),
      zipCode: trimmed(form.zipCode),
    };

    const base = {
      email: form.email.trim(),
      phone: form.phone.trim(),
      password: form.password,
      otpChannel,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      ...address,
    };

    if (role === UserRole.JOB_POSTER) {
      return {
        ...base,
        role: UserRole.JOB_POSTER,
        professionalTitle: trimmed(form.professionalTitle),
        streetAddress: trimmed(form.streetAddress),
        bio: trimmed(form.bio),
      };
    }

    if (role === UserRole.PARENT) {
      return { ...base, role: UserRole.PARENT };
    }

    return {
      ...base,
      role: UserRole.TEEN,
      dateOfBirth: form.dateOfBirth,
      parentEmail: trimmed(form.parentEmail),
      bio: trimmed(form.bio),
      resumeUrl: trimmed(form.resumeUrl),
      ...(skillIds.length > 0 ? { skillIds } : {}),
      availability: toAvailability(work),
      workPreferences: toWorkPreferences(work),
    };
  }

  /* --- validation -------------------------------------------------------- */

  /**
   * Checks only the fields the current step owns, so a registrant is never
   * blocked by something they have not been asked for yet. The full payload is
   * re-checked against the shared schema before it is sent.
   */
  function validateStep(): boolean {
    const found: Record<string, string> = {};

    if (step === 'about') {
      if (!form.firstName.trim()) found.firstName = 'Enter your first name';
      if (!form.lastName.trim()) found.lastName = 'Enter your last name';

      if (role === UserRole.TEEN) {
        if (!form.dateOfBirth) {
          found.dateOfBirth = 'Enter your date of birth';
        } else if (age === null) {
          found.dateOfBirth = 'That is not a valid date';
        } else if (age < DEFAULT_MIN_TEEN_AGE) {
          found.dateOfBirth = `You need to be at least ${DEFAULT_MIN_TEEN_AGE} to join`;
        } else if (age > 120) {
          found.dateOfBirth = 'That is not a valid date';
        }

        // A minor may never hold an unlinked account. [FR-M01.2]
        if (isMinor && !form.parentEmail.trim()) {
          found.parentEmail = 'Under 18s need a guardian to approve the account';
        }
      }

      if (form.parentEmail.trim() && !emailSchema.safeParse(form.parentEmail).success) {
        found.parentEmail = 'Enter a valid email address';
      }

      if (form.zipCode.trim()) {
        const zip = zipCodeSchema.safeParse(form.zipCode);
        if (!zip.success) found.zipCode = zip.error.issues[0]?.message ?? 'Enter a valid ZIP code';
      }
    }

    if (step === 'account') {
      if (!emailSchema.safeParse(form.email).success) {
        found.email = 'Enter a valid email address';
      }
      const phone = phoneSchema.safeParse(form.phone);
      if (!phone.success) {
        found.phone = phone.error.issues[0]?.message ?? 'Enter a valid mobile number';
      }
      const password = passwordSchema.safeParse(form.password);
      if (!password.success) {
        found.password = password.error.issues[0]?.message ?? 'Choose a stronger password';
      }
    }

    setFieldErrors(found);
    return Object.keys(found).length === 0;
  }

  /** Shows the alert and brings it into view on the next paint. */
  function raise(message: string, fields: Record<string, string> = {}): void {
    setError(message);
    if (Object.keys(fields).length > 0) {
      setFieldErrors((current) => ({ ...current, ...fields }));
    }
    requestAnimationFrame(() => {
      alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      alertRef.current?.focus({ preventScroll: true });
    });
  }

  /** Moves to whichever step owns `field`, if it is not the current one. */
  function jumpToFieldStep(field: string | undefined): void {
    const owner = field ? STEP_OF_FIELD[field] : undefined;
    if (!owner) return;
    const index = steps.indexOf(owner);
    if (index >= 0) setStepIndex(index);
  }

  /** Presents an api rejection: banner, per-field messages, and the right step. */
  function showApiError(caught: unknown): void {
    const presented = presentApiError(caught, 'Could not create the account');
    setAccountExists(presented.accountExists);
    if (presented.accountExists) jumpToFieldStep('email');
    else jumpToFieldStep(Object.keys(presented.fields)[0]);
    raise(presented.message, presented.fields);
  }

  /* --- navigation & submit ----------------------------------------------- */

  function goNext(): void {
    setError(null);
    if (!validateStep()) return;
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  }

  function goBack(): void {
    setError(null);
    setStepIndex((current) => Math.max(current - 1, 0));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setAccountExists(false);

    // Not the last step yet — the primary button advances instead of submitting.
    if (!isLastStep) {
      goNext();
      return;
    }

    if (!validateStep()) {
      raise('Please fix the highlighted fields before continuing');
      return;
    }

    if (!acceptedTerms) {
      raise('Please accept the terms to create your account');
      return;
    }

    // Validated with the same schema the api uses, before the round trip.
    const parsed = registerInputSchema.safeParse(buildInput());
    if (!parsed.success) {
      const issues: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const field = String(issue.path[0] ?? '');
        if (field && !issues[field]) issues[field] = issue.message;
      }
      setFieldErrors(issues);

      jumpToFieldStep(Object.keys(issues)[0]);
      raise(parsed.error.issues[0]?.message ?? 'Please check the form');
      return;
    }

    setBusy(true);
    try {
      const result = await register(parsed.data as RegisterInput);
      // Only the identifier travels in the URL. The code itself is read from
      // the inbox — putting it in the address bar would defeat verification and
      // leave it in browser history.
      const query = new URLSearchParams({ identifier: result.sentTo });
      navigate(`/verify?${query.toString()}`);
    } catch (caught) {
      showApiError(caught);
    } finally {
      setBusy(false);
    }
  }

  /* --- render ------------------------------------------------------------ */

  const stateOptions = (
    <>
      <option value="">Select a state</option>
      {US_STATES.map((entry) => (
        <option key={entry.code} value={entry.code}>
          {entry.name}
        </option>
      ))}
    </>
  );

  return (
    <SignupLayout>
      <form onSubmit={handleSubmit} noValidate>
        <div className="rounded-3xl border border-line bg-white p-6 shadow-lift sm:p-8">
          <Stepper
            steps={stepDescriptors}
            current={stepIndex}
            onJump={(index) => {
              setError(null);
              setStepIndex(index);
            }}
          />

          <div className="mt-7 space-y-5">
            <div ref={alertRef} tabIndex={-1} className="scroll-mt-24 outline-none">
              <ErrorNote
                message={error}
                action={
                  accountExists ? (
                    <Link to="/login" className="font-bold underline underline-offset-2">
                      Sign in instead
                    </Link>
                  ) : undefined
                }
              />
            </div>

            {step === 'role' && (
              <section className="space-y-5">
                <header>
                  <h1 className="text-2xl font-extrabold text-ink">Create your account</h1>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                    Pick the option that fits you. It decides what you see next — and you cannot
                    change it later, so choose carefully.
                  </p>
                </header>

                <div className="space-y-3">
                  {ROLE_OPTIONS.map((option) => (
                    <OptionCard
                      key={option.value}
                      name="role"
                      value={option.value}
                      checked={role === option.value}
                      onSelect={(value) => chooseRole(value as SelfServeRole)}
                      icon={option.icon}
                      title={option.title}
                      description={option.description}
                    />
                  ))}
                </div>
              </section>
            )}

            {step === 'about' && (
              <section className="space-y-5">
                <header>
                  <h1 className="text-2xl font-extrabold text-ink">About you</h1>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                    This is what people see on your profile. Only your name is required.
                  </p>
                </header>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="First name"
                    value={form.firstName}
                    onChange={set('firstName')}
                    error={fieldErrors.firstName}
                    autoComplete="given-name"
                    required
                  />
                  <Input
                    label="Last name"
                    value={form.lastName}
                    onChange={set('lastName')}
                    error={fieldErrors.lastName}
                    autoComplete="family-name"
                    required
                  />
                </div>

                {role === UserRole.TEEN && (
                  <>
                    <Input
                      label="Date of birth"
                      type="date"
                      value={form.dateOfBirth}
                      onChange={set('dateOfBirth')}
                      error={fieldErrors.dateOfBirth}
                      hint={
                        age !== null && age >= DEFAULT_MIN_TEEN_AGE
                          ? `You are ${age} — ${isMinor ? 'a guardian has to approve your account' : 'no guardian approval needed'}.`
                          : 'We use this to decide whether a guardian needs to approve your account.'
                      }
                      max={new Date().toISOString().slice(0, 10)}
                      required
                    />

                    {isMinor && (
                      <InfoNote>
                        You are under 18, so we will email your parent or guardian to approve the
                        account before you can apply for work.
                      </InfoNote>
                    )}

                    <Input
                      label="Parent or guardian email"
                      type="email"
                      value={form.parentEmail}
                      onChange={set('parentEmail')}
                      error={fieldErrors.parentEmail}
                      optional={!isMinor}
                      hint="We send them an approval request."
                      autoComplete="email"
                    />
                  </>
                )}

                {role === UserRole.JOB_POSTER && (
                  <>
                    <Input
                      label="Professional title"
                      value={form.professionalTitle}
                      onChange={set('professionalTitle')}
                      error={fieldErrors.professionalTitle}
                      optional
                      placeholder="Freelance tutor, independent contractor…"
                      hint="Shown beside the jobs you post."
                    />
                    <Input
                      label="Street address"
                      value={form.streetAddress}
                      onChange={set('streetAddress')}
                      error={fieldErrors.streetAddress}
                      optional
                      autoComplete="street-address"
                      hint="Never shown publicly — used for verification and logistics."
                    />
                  </>
                )}

                <div className="grid gap-4 sm:grid-cols-[1.4fr_1fr_1fr]">
                  <Input
                    label="City"
                    value={form.city}
                    onChange={set('city')}
                    error={fieldErrors.city}
                    optional
                    autoComplete="address-level2"
                  />
                  <SelectInput
                    label="State"
                    value={form.state}
                    onChange={set('state')}
                    error={fieldErrors.state}
                    optional
                  >
                    {stateOptions}
                  </SelectInput>
                  <Input
                    label="ZIP"
                    value={form.zipCode}
                    onChange={set('zipCode')}
                    error={fieldErrors.zipCode}
                    optional
                    inputMode="numeric"
                    placeholder="94105"
                    autoComplete="postal-code"
                  />
                </div>

                {role !== UserRole.PARENT && (
                  <Textarea
                    label={role === UserRole.TEEN ? 'Short bio' : 'About your work'}
                    value={form.bio}
                    onChange={set('bio')}
                    error={fieldErrors.bio}
                    optional
                    maxLength={300}
                    placeholder={
                      role === UserRole.TEEN
                        ? 'A couple of lines about you — what you enjoy, what you are good at.'
                        : 'What you hire for, and what it is like to work with you.'
                    }
                    hint={`${form.bio.length}/300 characters`}
                  />
                )}
              </section>
            )}

            {step === 'work' && (
              <section className="space-y-6">
                <header>
                  <h1 className="text-2xl font-extrabold text-ink">What can you do?</h1>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                    Job posters search on these. Every field here is optional — you can fill it in
                    later from your profile.
                  </p>
                </header>

                <div>
                  <SkillPicker
                    skills={skillsQuery.data ?? []}
                    selected={skillIds}
                    onChange={setSkillIds}
                    loading={skillsQuery.isPending}
                    failed={skillsQuery.isError}
                  />
                  {fieldErrors.skillIds && (
                    <p className="mt-2 text-xs font-medium text-red-600">{fieldErrors.skillIds}</p>
                  )}
                </div>

                <AvailabilityFields value={work} onChange={setWork} />

                <Input
                  label="Résumé link"
                  type="url"
                  value={form.resumeUrl}
                  onChange={set('resumeUrl')}
                  error={fieldErrors.resumeUrl}
                  optional
                  placeholder="https://…"
                  hint="A link to a résumé or portfolio, if you have one."
                />

                <Textarea
                  label="Anything else about your availability"
                  value={work.notes}
                  onChange={(event) => setWork({ ...work, notes: event.target.value })}
                  optional
                  maxLength={200}
                  placeholder="e.g. term-time only, or not before 4pm on school days."
                />
              </section>
            )}

            {step === 'account' && (
              <section className="space-y-5">
                <header>
                  <h1 className="text-2xl font-extrabold text-ink">Sign-in details</h1>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                    We need both an email and a mobile number. You choose which one gets your
                    verification code.
                  </p>
                </header>

                <Input
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  error={fieldErrors.email}
                  autoComplete="email"
                  required
                />

                <Input
                  label="Mobile"
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  error={fieldErrors.phone}
                  placeholder="+14155550132"
                  hint="International format, starting with +"
                  autoComplete="tel"
                  required
                />

                <div>
                  <Input
                    label="Password"
                    type="password"
                    value={form.password}
                    onChange={set('password')}
                    error={fieldErrors.password}
                    autoComplete="new-password"
                    hint={`At least ${PASSWORD_MIN_LENGTH} characters. A short phrase works well.`}
                    required
                  />
                  <PasswordMeter value={form.password} />
                </div>

                <div>
                  <p className="mb-2.5 text-sm font-semibold text-ink">
                    Where should we send your code?
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <OptionCard
                      name="otpChannel"
                      value={OtpChannel.EMAIL}
                      checked={otpChannel === OtpChannel.EMAIL}
                      onSelect={() => setOtpChannel(OtpChannel.EMAIL)}
                      icon={<BellIcon className="size-5" />}
                      title="Email"
                      description={form.email.trim() || 'To the address above'}
                    />
                    <OptionCard
                      name="otpChannel"
                      value={OtpChannel.SMS}
                      checked={otpChannel === OtpChannel.SMS}
                      onSelect={() => setOtpChannel(OtpChannel.SMS)}
                      icon={<ChatIcon className="size-5" />}
                      title="Text message"
                      description={form.phone.trim() || 'To the number above'}
                    />
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-line bg-canvas p-3.5">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(event) => setAcceptedTerms(event.target.checked)}
                    className="mt-0.5 size-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-xs leading-relaxed text-slate-600">
                    I agree to the TeenTack terms of service and privacy policy, and confirm the
                    details above are mine.
                  </span>
                </label>
              </section>
            )}
          </div>

          {/* Wizard controls */}
          <div className="mt-8 flex items-center gap-3 border-t border-line pt-6">
            {stepIndex > 0 && (
              <Button type="button" variant="outline" onClick={goBack} disabled={busy}>
                Back
              </Button>
            )}

            <Button
              type="submit"
              size="lg"
              className="flex-1"
              disabled={busy}
              aria-busy={busy || undefined}
            >
              {busy ? 'Creating your account…' : isLastStep ? 'Create account' : 'Continue'}
              {!busy && !isLastStep && <ArrowRightIcon className="size-4" />}
            </Button>
          </div>
        </div>
      </form>
    </SignupLayout>
  );
}
